export function preserveUnchanged<TValue>(previous: TValue | undefined, next: TValue): TValue {
  if (previous === undefined || previous === next) {
    return next;
  }

  if (Array.isArray(previous) && Array.isArray(next)) {
    return preserveUnchangedItems(previous, next) as TValue;
  }

  if (isPlainObject(previous) && isPlainObject(next)) {
    return preserveUnchangedProperties(previous, next) as TValue;
  }

  return next;
}

function preserveUnchangedItems(previous: readonly unknown[], next: readonly unknown[]): readonly unknown[] {
  const preserved = next.map((item, index) => preserveUnchanged(previous[index], item));

  return preserved.length === previous.length && preserved.every((item, index) => item === previous[index])
    ? previous
    : preserved;
}

function preserveUnchangedProperties(
  previous: Record<string, unknown>,
  next: Record<string, unknown>,
): Record<string, unknown> {
  const preserved: Record<string, unknown> = {};
  let sameAsPrevious = Object.keys(previous).length === Object.keys(next).length;

  for (const [key, value] of Object.entries(next)) {
    preserved[key] = preserveUnchanged(previous[key], value);
    sameAsPrevious &&= preserved[key] === previous[key];
  }

  return sameAsPrevious ? previous : preserved;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
